import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { IconCheck, IconChevron } from './icons.js';
import { cx } from './cx.js';

/**
 * Select da casa.
 *
 * O `<select>` nativo nao aceita estilo na lista: o Chrome desenha um menu
 * branco do sistema no meio de um app escuro, com a fonte dele e o espacamento
 * dele. Em toda tela onde escolher e o passo principal — a unidade no registro
 * de encomenda, o bloco no cadastro — o controle mais usado era justamente o
 * unico que nao parecia parte do produto.
 *
 * Isto e um combobox de verdade, entao ele carrega o que o nativo dava de
 * graca e nao pode se perder no caminho: navegacao por setas, Home/End, Esc,
 * clique fora, foco visivel e leitura por leitor de tela (`role="listbox"` +
 * `aria-activedescendant`).
 *
 * A busca aparece sozinha a partir de 8 opcoes. Nao e enfeite: um condominio
 * de 200 apartamentos e a lista mais rolada do produto, e o nativo so oferecia
 * digitar a primeira letra e torcer.
 */

export interface SelectOption {
  value: string;
  label: string;
  /** Texto secundario a direita — "12 unidades", "Torre A". */
  hint?: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Rotulo do estado vazio. Vira a primeira opcao quando `emptyOption`. */
  placeholder?: string;
  /**
   * Inclui uma opcao de valor "" no topo — o `<option value="">Todos</option>`
   * de antes. Sem isso, nao ha como voltar a "nenhum" depois de escolher.
   */
  emptyOption?: string;
  disabled?: boolean;
  /** Forca a busca; por padrao ela aparece a partir de 8 opcoes. */
  searchable?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

const SEARCH_THRESHOLD = 8;

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Selecione...',
  emptyOption,
  disabled = false,
  searchable,
  className,
  id,
  'aria-label': ariaLabel,
}: SelectProps) {
  const generatedId = useId();
  const listId = `${id ?? generatedId}-list`;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(0);
  const [dropUp, setDropUp] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const all = useMemo<SelectOption[]>(
    () => (emptyOption === undefined ? options : [{ value: '', label: emptyOption }, ...options]),
    [options, emptyOption],
  );

  const showSearch = searchable ?? all.length > SEARCH_THRESHOLD;

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return all;
    return all.filter((option) =>
      `${option.label} ${option.hint ?? ''}`.toLowerCase().includes(term),
    );
  }, [all, search]);

  const selected = all.find((option) => option.value === value);

  // Abrir posiciona o destaque na opcao atual — nao no topo. Reabrir uma lista
  // de 200 unidades no comeco obrigaria a rolar ate a escolha de novo.
  function openMenu() {
    if (disabled) return;
    const index = visible.findIndex((option) => option.value === value);
    setActive(index >= 0 ? index : 0);
    setSearch('');

    // Espaco abaixo do campo: perto do rodape a lista abre para cima em vez de
    // sair da tela.
    const rect = buttonRef.current?.getBoundingClientRect();
    setDropUp(rect ? window.innerHeight - rect.bottom < 280 && rect.top > 280 : false);

    setOpen(true);
  }

  function close({ focusButton = true }: { focusButton?: boolean } = {}) {
    setOpen(false);
    setSearch('');
    if (focusButton) buttonRef.current?.focus();
  }

  function pick(option: SelectOption) {
    onChange(option.value);
    close();
  }

  // Foco no campo de busca ao abrir; sem ele, digitar nao chega a lugar nenhum.
  useEffect(() => {
    if (open && showSearch) searchRef.current?.focus();
  }, [open, showSearch]);

  // Clique fora e rolagem da pagina fecham: o menu e posicionado junto ao campo
  // e ficaria flutuando solto se a pagina andasse por baixo dele.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent | TouchEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  // Mantem a opcao destacada dentro da area visivel da lista.
  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  function onKeyDown(event: React.KeyboardEvent) {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openMenu();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((index) => Math.min(index + 1, visible.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((index) => Math.max(index - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        setActive(0);
        break;
      case 'End':
        event.preventDefault();
        setActive(visible.length - 1);
        break;
      case 'Enter': {
        event.preventDefault();
        const option = visible[active];
        if (option) pick(option);
        break;
      }
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        // Tab continua andando pelo formulario; so o menu se fecha.
        close({ focusButton: false });
        break;
      default:
        break;
    }
  }

  return (
    <div ref={rootRef} className={cx('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        // A opcao destacada precisa ser anunciada pelo elemento QUE TEM O FOCO,
        // e nao pela lista: com a busca ligada o foco esta no campo de texto, e
        // e la que o atributo vale. No `<ul>` — onde estava — nenhum leitor de
        // tela falava a opcao sob as setas.
        aria-activedescendant={open && !showSearch ? `${listId}-${active}` : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? close() : openMenu())}
        // Sempre no botao: com a busca ligada o foco vai para o campo de texto
        // ao abrir, mas com o menu fechado e o botao quem recebe a seta.
        onKeyDown={onKeyDown}
        className={cx(
          'flex w-full items-center justify-between gap-3 rounded-2xl border bg-surface-2 px-4 py-3 text-left text-fg transition',
          'disabled:cursor-not-allowed disabled:opacity-60',
          open ? 'border-accent/60' : 'border-line hover:border-faint',
        )}
      >
        <span className={cx('min-w-0 truncate', !selected && 'text-faint')}>
          {selected?.label ?? placeholder}
        </span>
        <IconChevron
          aria-hidden
          className={cx(
            'size-4 shrink-0 text-faint transition-transform',
            open && '-rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          // O campo costuma viver dentro de um <label>, e clique dentro de
          // label e reencaminhado ao controle rotulado — sem barrar aqui,
          // escolher uma opcao fechava e reabria o menu no mesmo gesto.
          onClick={(event) => event.stopPropagation()}
          className={cx(
            // `min-w-max` fazia uma opcao longa alargar a pagina inteira em
            // celulares estreitos. A lista acompanha o controle e os rotulos
            // internos cuidam do truncamento.
            'absolute z-50 w-full rounded-2xl border border-line bg-surface p-1 shadow-2xl shadow-ink/60',
            dropUp ? 'bottom-full mb-2' : 'top-full mt-2',
          )}
        >
          {showSearch && (
            <input
              ref={searchRef}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Buscar..."
              aria-label="Buscar opcao"
              aria-controls={listId}
              aria-activedescendant={`${listId}-${active}`}
              className="mb-1 w-full rounded-xl border border-line bg-surface-2 px-3 py-2 text-sm text-fg placeholder:text-faint focus:border-accent/60 focus:outline-none"
            />
          )}

          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            tabIndex={-1}
            className="max-h-64 overflow-y-auto"
          >
            {visible.length === 0 && (
              <li className="px-3 py-2.5 text-sm text-faint">Nada encontrado.</li>
            )}

            {visible.map((option, index) => {
              const isSelected = option.value === value;
              return (
                <li
                  key={option.value || '__empty'}
                  id={`${listId}-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActive(index)}
                  // mousedown, e nao click: o `pointerdown` de fora ja teria
                  // fechado o menu antes do clique completar.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    pick(option);
                  }}
                  className={cx(
                    'flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition',
                    index === active ? 'bg-surface-2' : '',
                    isSelected ? 'font-bold text-accent' : 'text-fg',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {option.hint && (
                    <span className="shrink-0 text-xs text-faint">{option.hint}</span>
                  )}
                  {isSelected && <IconCheck className="size-4 shrink-0" />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
